import { useTranslation } from '../presentation/common/useTranslation.js';
import GoodvaleWordmark from './GoodvaleWordmark';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ArrowRight, X } from 'lucide-react';
import './DialogProvider.css';

import { DialogContext } from '../hooks/useDialog';

function Dialog({ request, onFinish }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const inputRef = useRef(null);
  const cancelRef = useRef(null);
  const id = useId();
  const [value, setValue] = useState('');
  const { type, message, options } = request;
  const isPrompt = type === 'prompt';
  const danger = options.danger;
  const valid = !isPrompt || (value.trim().length >= (options.minLength ?? 1) && (options.expectedValue === undefined || value === options.expectedValue));
  const cancel = () => onFinish(isPrompt ? null : false);

  useEffect(() => {
    const element = ref.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = 'hidden';
    (inputRef.current || cancelRef.current)?.focus();
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return createPortal(
    <dialog ref={ref} className={`gw-dialog ${danger ? 'gw-dialog-danger' : ''}`} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} onCancel={event => { event.preventDefault(); cancel(); }}>
      <form onSubmit={event => { event.preventDefault(); if (valid) onFinish(isPrompt ? value.trim() : true); }}>
        <div className="gw-dialog-content">
          <div className="gw-dialog-topline"><GoodvaleWordmark className="w-28" /><button type="button" className="gw-dialog-close" onClick={cancel} aria-label={t('dialogs.close')}><X size={20} /></button></div>
          <h2 id={`${id}-title`}>{options.title || t(`dialogs.${type}_title`)}</h2>
          <p id={`${id}-description`} className="gw-dialog-description">{message}</p>
          {isPrompt && <div className="gw-dialog-field">
            <label htmlFor={`${id}-input`}>{options.label || t('dialogs.reason')}</label>
            {options.multiline === false
              ? <input ref={inputRef} id={`${id}-input`} autoComplete="off" maxLength={options.maxLength || 2000} value={value} onChange={event => setValue(event.target.value)} placeholder={options.placeholder || ''} />
              : <textarea ref={inputRef} id={`${id}-input`} rows={5} maxLength={options.maxLength || 2000} value={value} onChange={event => setValue(event.target.value)} placeholder={options.placeholder || t('dialogs.placeholder')} />}
            {options.minLength > 1 && <p className="gw-dialog-hint" aria-live="polite">{t('dialogs.minimum', { count: options.minLength, current: value.trim().length })}</p>}
          </div>}
        </div>
        <div className="gw-dialog-actions">
          {type !== 'alert' && <button ref={cancelRef} type="button" className="gw-dialog-secondary" onClick={cancel}>{t('dialogs.cancel')}</button>}
          <button type="submit" className="gw-dialog-primary" disabled={!valid}>{options.confirmLabel || t(type === 'alert' ? 'dialogs.ok' : isPrompt ? 'dialogs.submit' : 'dialogs.confirm')}<ArrowRight size={16} /></button>
        </div>
      </form>
    </dialog>, document.body,
  );
}

export default function DialogProvider({ children }) {
  const [requests, setRequests] = useState([]);
  const pending = useRef(new Set());
  const sequence = useRef(0);
  const ask = useCallback((type, message, options = {}) => new Promise(resolve => {
    const request = { id: ++sequence.current, type, message, options, resolve };
    pending.current.add(request);
    setRequests(current => [...current, request]);
  }), []);
  const api = useMemo(() => ({
    alert: (message, options) => ask('alert', message, options),
    confirm: (message, options) => ask('confirm', message, options),
    prompt: (message, options) => ask('prompt', message, options),
  }), [ask]);
  useEffect(() => () => {
    for (const request of pending.current) request.resolve(request.type === 'prompt' ? null : false);
    pending.current.clear();
  }, []);
  const finish = result => {
    const request = requests[0];
    if (!pending.current.delete(request)) return;
    setRequests(current => current.filter(item => item !== request));
    request.resolve(result);
  };
  return <DialogContext.Provider value={api}>{children}{requests[0] && <Dialog key={requests[0].id} request={requests[0]} onFinish={finish} />}</DialogContext.Provider>;
}
